<script lang="ts">
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import SelectFilter from "@shared/svelte/Components/SelectFilter.svelte";
  import SvelteInfiniteScroll from "@shared/svelte/Components/InfiniteScroll.svelte";
  import TableSortControl from "@shared/svelte/Components/TableSortControl.svelte";
  import UIRouter from "@shared/svelte/Components/UIRouterLink.svelte";
  import { debounce, merge } from "lodash";
  import { params } from "@roxi/routify";
  import type { RetailerIndexType } from "@shared/types/Retailer";
  import moment from "moment";

  export let refreshState: () => void;
  export let retailers: RetailerIndexType[];
  export let filters: { [key: string]: string };
  export let loadMoreRetailers: () => void;

  const orderDays: { label: string; value: string | null }[] = [
    { label: "All Order Days", value: null },
    { label: "Monday", value: "Monday" },
    { label: "Tuesday", value: "Tuesday" },
    { label: "Wednesday", value: "Wednesday" },
    { label: "Thursday", value: "Thursday" },
    { label: "Friday", value: "Friday" },
    { label: "Saturday", value: "Saturday" },
    { label: "Sunday", value: "Sunday" },
    { label: "Unspecified", value: "unspecified" },
  ];

  const runFilters = function () {
    refreshState();
  };

  const runSearch = debounce(runFilters, 350);
  const debouncedLoadMoreRetailers = debounce(loadMoreRetailers, 350);

  $: sortFilters = {
    type: $params.order || "last_ordered_at",
    direction: $params.direction || "DESC",
    sortCallBack: function (sort: any) {
      merge(filters, { order: sort.order, direction: sort.direction });
      runSearch();
    },
  };

  $: scrollable = retailers.length && retailers.length >= 10;
  let scrollElementContainer: Element;

  function doScroll() {
    debouncedLoadMoreRetailers();
  }

  $: lessResults = retailers.length && retailers.length < 20;
</script>

<style>
  .table-container {
    overflow-y: auto;
  }
  .added-height {
    height: 80vh;
  }
</style>

<div class="fizz-styles" style="padding: 24px;">
  <div class="fizz-flex-row-responsive fizz-surface fizz-inset-square-16">
    <input
      type="text"
      name="search"
      id="distributor-retailers-search-input"
      placeholder="Search by name or account number"
      class="fizz-input-search fizz-full-width"
      style="--max-width: 40%"
      bind:value={filters.search}
      on:keyup={runSearch}
    />

    <SelectFilter
      fontColor={"rgb(0, 43, 72)"}
      selectId={"order-day"}
      options={orderDays}
      bind:selectedValue={filters.order_day}
      onChange={runSearch}
    ></SelectFilter>

    <DatePicker
      bind:startDate={filters.start_date}
      bind:endDate={filters.end_date}
      defaultButtonLabel={"Last Order Date Filter"}
      runFilters={runSearch}
      minWidth={"275px"}
    ></DatePicker>
  </div>

  <div
    class="added-height {scrollable ? 'table-container' : ''}"
    bind:this={scrollElementContainer}
  >
    <table
      class="fizz-table fizz-stack-16 {!lessResults ? 'added-height' : ''}"
      class:fizz-table-highlight-rows={retailers.length > 0}
    >
      <thead>
        <tr>
          <th>Account Number</th>
          <th width="45%">
            <div class="fizz-flex-row">
              Retailer Name
              <TableSortControl bind:sortFilter={sortFilters} colName={"name"} />
            </div>
          </th>
          <th>
            <div class="fizz-flex-row">
              Last Order
              <TableSortControl bind:sortFilter={sortFilters} colName={"last_ordered_at"} />
            </div>
          </th>
          <th width="10%">Order Day</th>
          <th class="fizz-align-right" width="20%">
            <div class="fizz-flex-row fizz-justify-end">
              Total Orders
              <TableSortControl bind:sortFilter={sortFilters} colName={"num_orders"} />
            </div>
          </th>
        </tr>
      </thead>
      {#if retailers.length > 0}
        <tbody>
          {#each retailers as retailer}
            <UIRouter
              stateName="layout.retailer_details"
              params={{ retailerId: retailer.id }}
              tableRow={true}
              id={"retailer-" + retailer.id}
              classes={"cursor-pointer"}
            >
              <td data-label="Account Number"
                >{retailer.account_number ? retailer.account_number : "--"}</td
              >
              <td data-label="Retailer Name">{retailer.name}</td>
              <td data-label="Last Order"
                >{retailer.last_ordered_at
                  ? moment(retailer.last_ordered_at, "YYYYMMDD").fromNow()
                  : "--"}</td
              >
              <td data-label="Order Day">{retailer.order_day ? retailer.order_day : "--"}</td>
              <td data-label="Total Orders" class="fizz-align-right">
                {retailer.num_orders}
              </td>
            </UIRouter>
          {/each}
          {#if scrollElementContainer}
            <SvelteInfiniteScroll
              threshold={0}
              on:loadMore={() => doScroll()}
              bind:elementScroll={scrollElementContainer}
            />
          {/if}
        </tbody>
      {:else}
        <tbody>
          <tr class="fizz-text-center">
            <td class="layout-1" colspan={5}>
              {#if filters.search || filters.order_day || filters.start_date || filters.end_date}
                <div class="padding--small">
                  There are no retailers that meet that search criteria, please modify your search.
                </div>
              {:else}
                <strong class="padding--small"> No Retailers </strong>
                <div class="padding--smaller">
                  There are no Retailers associated with your account.
                </div>
              {/if}
            </td>
          </tr>
        </tbody>
      {/if}
    </table>
  </div>
</div>
